import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Dialog, DialogTitle, DialogContent, Button, Typography, List, ListItemText, ListItem } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { NonFetchableEntity } from '../../utils/planJsonExportImportUtils';

interface FetchingInfoModalProps {
  nonFetchableEntities: NonFetchableEntity[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const FetchingInfoModal: React.FC<FetchingInfoModalProps> = ({ nonFetchableEntities, isOpen, onClose, onConfirm }) => {
  const isPlural = useMemo(() => nonFetchableEntities.length > 1, [nonFetchableEntities]);
  const textureIds = useMemo(() => nonFetchableEntities.filter((nFE) => nFE.type === 'texture').map((fnFE) => String(fnFE.id)), [nonFetchableEntities]);
  const modelIds = useMemo(
    () => nonFetchableEntities.filter((nFE) => nFE.modelType === 'model2D' && nFE.type !== 'texture').map((fnFE) => String(fnFE.id)),
    [nonFetchableEntities],
  );
  const productIds = useMemo(
    () => nonFetchableEntities.filter((nFE) => nFE.modelType === 'product' && nFE.type !== 'texture').map((fnFE) => String(fnFE.id)),
    [nonFetchableEntities],
  );

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm">
      {/* Modal title */}
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <InfoOutlinedIcon color="info" />
          <FormattedMessage id="FetchingInfoModal.title" />
        </Box>
      </DialogTitle>

      {/* Main message*/}
      <DialogContent>
        <FormattedMessage id={isPlural ? 'FetchingInfoModal.messagePlural' : 'FetchingInfoModal.messageSingular'} />

        {/** Data section */}
        <Typography sx={{ marginTop: '0.25rem' }}>
          <FormattedMessage id={'FetchingInfoModal.relatedData'} />
        </Typography>

        {/** Id listings */}
        <List>
          {/** Product listings */}
          {productIds.length > 0 && (
            <ListItem key={productIds.join(', ')}>
              <FormattedMessage id={'FetchingInfoModal.productIds'} /> &nbsp; <ListItemText primary={' ' + productIds.join(', ')} />
            </ListItem>
          )}

          {/** Model listings */}
          {modelIds.length > 0 && (
            <ListItem key={modelIds.join(', ')}>
              <FormattedMessage id={'FetchingInfoModal.modelIds'} /> &nbsp; <ListItemText primary={' ' + modelIds.join(', ')} />
            </ListItem>
          )}

          {/** Texture listings */}
          {textureIds.length > 0 && (
            <ListItem key={textureIds.join(', ')}>
              <FormattedMessage id={'FetchingInfoModal.textureIds'} /> &nbsp; <ListItemText primary={textureIds.join(', ')} />
            </ListItem>
          )}
        </List>

        {/* Removal question */}
        <Typography sx={{ marginTop: '1rem' }}>
          <FormattedMessage id={isPlural ? 'FetchingInfoModal.questionPlural' : 'FetchingInfoModal.questionSingular'} />
        </Typography>

        {/* Actions */}
        <Box display="flex" gap={2} justifyContent="flex-end">
          <Button onClick={onClose} color="inherit" variant="contained">
            <FormattedMessage id="FetchingInfoModal.no" />
          </Button>
          <Button onClick={onConfirm} color="secondary" variant="contained">
            <FormattedMessage id="FetchingInfoModal.yes" />
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FetchingInfoModal;
